import React, { useRef, useEffect } from 'react';
import jsPDF from 'jspdf';
import uplatnica from "src/assets/images/uplatnica.jpg";
import { formatNumber, useCheckoutHealthOfferGeneralTransferDetailsQuery } from 'src/common';
import { BarCode } from 'src/components';

export const usePaymentSlip = (offerId: string, paymentType: "oneTime" | "monthly") => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const barcodeRef = useRef<HTMLCanvasElement | null>(null);
  const isCanvasDrawn = useRef(false);

  const { loading, data } = useCheckoutHealthOfferGeneralTransferDetailsQuery({
    variables: { offerId, installments: paymentType === "oneTime" ? 1 : 12 },
    fetchPolicy: 'cache-first',
  });

  const { health } = data || {};
  const { checkoutHealthOfferGeneralTransferDetails: gtDetails } = health || {};

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !gtDetails || !barcodeRef.current || isCanvasDrawn.current) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const price = gtDetails.price ? formatNumber(paymentType === "oneTime" ? gtDetails.price : gtDetails.price / 12) : "";
    const innerPrice = `=${price}`;
    const outerPrice = `=${price} EUR`;

    const customerFullName = gtDetails.customerFirstName && gtDetails.customerLastName ? `${gtDetails.customerFirstName} ${gtDetails.customerLastName}` : 'Ime i prezime';
    const customerAddress = gtDetails.customerAddress || 'Ulica i broj';
    const customerZipCode = gtDetails.customerZipCode && gtDetails.customerZipCode !== '0' ? gtDetails.customerZipCode : 'Pošt. br.';
    const customerCity = gtDetails.customerCity || 'Grad';
    const customerCountry = gtDetails.customerCountry || 'Hrvatska';

    const recipientName = gtDetails.recipientName || '';
    const recipientAddress = gtDetails.recipientAddress || '';
    const recipientCity = gtDetails.recipientCity || '';
    const recipientZipCode = gtDetails.recipientZipCode || '';
    const iban = gtDetails.recipientAccountNumber || '';

    const description = gtDetails.description || 'Uplata dopunskog osiguranja';

    const referenceNumber = gtDetails.referenceNumber || gtDetails.referenceNumberType || 'Vaš oib';

    const model = gtDetails.model || '';

    const prefix = gtDetails.referencePrefix || null;
    const prefixLength = prefix ? prefix.toString().length : 0;
    const prefixPosition = prefixLength > 6 ? 1000 : 1020;

    const modelAndReferenceNumber = `${model} - ${prefix ?? ''}${referenceNumber}`;

    const imageObj = new Image();
    imageObj.onload = function () { // eslint-disable-line func-names
      ctx.drawImage(imageObj, 1, 2, 1920, 806);

      ctx.font = '32px Arial';
      ctx.fillText(customerFullName, 75, 130);
      ctx.fillText(customerAddress, 75, 165);
      ctx.fillText(customerZipCode, 75, 200);
      ctx.fillText(customerCity, 200, 200);
      ctx.fillText(customerCountry, 75, 230);

      ctx.fillText(recipientName, 75, 365);
      ctx.fillText(recipientAddress, 75, 400);
      ctx.fillText(recipientCity, 200, 435);
      ctx.fillText(recipientZipCode, 75, 435);
      ctx.fillText('Hrvatska', 75, 465);
      ctx.fillText(iban, 935, 285);

      ctx.fillText(model, 515, 355);
      if (prefix) {
        ctx.fillText(prefix, prefixPosition, 355);
      }
      ctx.fillText(referenceNumber, 1120, 355);
      ctx.fillText(description, 820, 400);

      ctx.fillText(innerPrice, 1195, 100);

      ctx.fillText(outerPrice, 1680, 100);
      ctx.fillText(iban, 1460, 285);
      ctx.fillText(modelAndReferenceNumber, 1460, 355);
      ctx.fillText(description, 1400, 420);

      if (barcodeRef.current) {
        ctx.drawImage(barcodeRef.current, 70, 550, 500, 166);
      }

      isCanvasDrawn.current = true;
    };

    imageObj.src = uplatnica;
  };

  useEffect(() => {
    if (!loading && gtDetails && barcodeRef.current && !isCanvasDrawn.current) {
      drawCanvas();
    }
  }, [loading, gtDetails, barcodeRef.current]);

  const downloadSlip = () => {
    if (!canvasRef.current) return;

    const pdfPrep = canvasRef.current.toDataURL('image/jpeg', 1.0);
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px' });
    pdf.addImage(pdfPrep, 'JPEG', 0, 0, 450, 210);
    pdf.save('uplatnica-dopunskog-osiguranja.pdf');
  };

  const PaymentSlip: React.FC = () => {
    const price = gtDetails?.price
      ? formatNumber(paymentType === "oneTime" ? gtDetails.price : gtDetails.price / 12)
      : "";

    const handleBarcodeGenerated = (canvas: HTMLCanvasElement) => {
      barcodeRef.current = canvas;
    };

    return (
      <>
        <canvas
          ref={canvasRef}
          id="generalPaymentCanvas"
          data-testid="generalPaymentCanvas"
          width="1920"
          height="806"
          style={{ display: 'none' }}
        />
        <div style={{ display: 'none' }}>
          <BarCode
            price={price}
            userFirstName={gtDetails?.customerFirstName || ''}
            userLastName={gtDetails?.customerLastName || ''}
            userAddress={gtDetails?.customerAddress || ''}
            userCity={gtDetails?.customerCity || ''}
            oib={gtDetails?.customerOib || ''}
            recipient={gtDetails?.recipientName || ''}
            recipientAddress={gtDetails?.recipientAddress || ''}
            recipientCity={gtDetails?.recipientCity || ''}
            model={gtDetails?.model || ''}
            iban={gtDetails?.recipientAccountNumber || ''}
            description={gtDetails?.description || 'Uplata dopunskog osiguranja'}
            hidden={false}
            onBarcodeGenerated={handleBarcodeGenerated}
          />
        </div>
      </>
    )
  };

  return {
    downloadSlip,
    loading,
    PaymentSlip,
  };
};